import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  ExternalLink,
  Crown,
  AlertCircle,
  FileText,
  Trash2,
  Download,
  ArrowRight,
  Lock,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { getPaddleEnvironment } from "@/lib/paddle";
import { getCustomerPortalUrl } from "@/utils/billing.functions";
import { getMyUsage, listMyAnalyses, deleteMyAnalysis } from "@/utils/analyzer.functions";
import { exportAnalysisPdf } from "@/lib/pdf-export";
import type { AnalysisResult } from "@/components/ContractAnalyzer";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type AnalysisRow = {
  id: string;
  title: string | null;
  risk_level: string | null;
  risk_score: number | null;
  tool_slug: string | null;
  created_at: string;
  result: AnalysisResult | null;
};

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Your Account — legaldecoder" },
      { name: "description", content: "Manage your legaldecoder plan, billing, and usage." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const { session, user, loading: authLoading } = useAuth();
  const { subscription, isPro, isActive, credits, loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [analyses, setAnalyses] = useState<AnalysisRow[] | null>(null);
  const [analysesLoading, setAnalysesLoading] = useState(false);
  const accessToken = session?.access_token;

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/auth", search: { redirect: "/account" } as never });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user || !accessToken) return;
    // Trigger a usage fetch to keep server-side cache warm. The real credit
    // balance is sourced from useSubscription (which is realtime).
    getMyUsage({ headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => {
      /* noop */
    });
  }, [user, accessToken]);

  useEffect(() => {
    if (!user || !accessToken) return;
    setAnalysesLoading(true);
    listMyAnalyses({ headers: { Authorization: `Bearer ${accessToken}` } })
      .then((data) => setAnalyses(data as unknown as AnalysisRow[]))
      .catch(() => setAnalyses([]))
      .finally(() => setAnalysesLoading(false));
  }, [user, accessToken]);

  const handleDeleteAnalysis = async (id: string) => {
    if (!confirm("Delete this analysis?")) return;
    if (!accessToken) {
      toast.info("Please sign in again to delete this analysis.");
      navigate({ to: "/auth", search: { redirect: "/account" } as never });
      return;
    }
    try {
      await deleteMyAnalysis({ data: { id }, headers: { Authorization: `Bearer ${accessToken}` } });
      setAnalyses((prev) => prev?.filter((r) => r.id !== id) ?? null);
      toast.success("Deleted");
    } catch {
      toast.error("Couldn't delete analysis.");
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Welcome to Pro! Your subscription is active.");
      // Clean URL
      window.history.replaceState({}, "", "/account");
    }
  }, []);

  const openPortal = async () => {
    if (!accessToken) {
      toast.info("Please sign in again to manage billing.");
      navigate({ to: "/auth", search: { redirect: "/account" } as never });
      return;
    }
    setPortalLoading(true);
    try {
      const res = await getCustomerPortalUrl({
        data: { environment: getPaddleEnvironment() },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        toast.error("No active subscription to manage.");
        return;
      }
      window.open(res.overviewUrl, "_blank");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't open billing portal.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (authLoading || subLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  const planLabel = isPro ? (subscription?.product_id === "teams_plan" ? "Teams" : "Pro") : "Free";

  const renewalDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight">Your account</h1>
        <p className="mt-2 text-muted-foreground">{user.email}</p>

        {subscription?.status === "past_due" && (
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 p-4 text-sm">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
            <div>
              <p className="font-semibold text-foreground">Payment needs attention</p>
              <p className="mt-1 text-muted-foreground">
                Your last charge didn't go through. Update your payment method to keep Pro active.
              </p>
            </div>
          </div>
        )}

        {/* Credits balance card */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Credits balance
              </p>
              <h2 className="mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight">
                <Crown className="h-6 w-6 text-primary-glow" />
                {credits} {credits === 1 ? "credit" : "credits"} remaining
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Each contract analysis uses 1 credit. Credits never expire on a monthly cycle — top
                up whenever you need more.
              </p>
            </div>
            <Button variant="hero" asChild className="gap-2">
              <Link to="/pricing">
                Buy credits
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {credits <= 0 && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 p-4 text-sm">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
              <p className="text-muted-foreground">
                You're out of credits. Purchase a credit pack to analyze your next document.
              </p>
            </div>
          )}

          {/* Legacy subscription billing portal — only show if the user still has
              an active legacy subscription on file. */}
          {isActive && (
            <div className="mt-6 border-t border-border pt-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Legacy subscription
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                You have an active legacy{" "}
                {subscription?.product_id === "teams_plan" ? "Teams" : "Pro"} subscription
                {renewalDate
                  ? ` (${subscription?.cancel_at_period_end ? "access until" : "renews"} ${renewalDate})`
                  : ""}
                .
              </p>
              <Button
                variant="outline"
                onClick={openPortal}
                disabled={portalLoading}
                className="mt-3 gap-2"
              >
                {portalLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                Manage billing & payment
              </Button>
            </div>
          )}
        </section>

        {/* My analyses */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                My analyses
              </p>
              <h2 className="mt-1 flex items-center gap-2 text-xl font-semibold">
                <FileText className="h-5 w-5 text-muted-foreground" />
                Saved analyses
              </h2>
            </div>
            {isPro && (
              <Button variant="hero" size="sm" asChild className="gap-1.5">
                <Link to="/">
                  New
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            )}
          </div>

          {!isPro ? (
            <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow shadow-elegant">
                <Lock className="h-5 w-5 text-primary-foreground" />
              </div>
              <p className="font-semibold text-foreground">Analysis history is a Pro feature</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Pro saves every analysis so you can revisit, re-export, and compare contracts later.
                Free analyses follow our zero-data-retention default and aren't stored.
              </p>
              <Button variant="hero" className="mt-4 gap-2" asChild>
                <Link to="/pricing">
                  Start 7-day Pro trial
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : analysesLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !analyses || analyses.length === 0 ? (
            <div className="mt-6 rounded-xl border border-dashed border-border p-8 text-center">
              <FileText className="mx-auto h-7 w-7 text-muted-foreground" />
              <p className="mt-3 font-semibold">No analyses yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Run your first analysis and we'll save it here.
              </p>
            </div>
          ) : (
            <ul className="mt-6 space-y-3">
              {analyses.map((r) => {
                const risk = (r.risk_level ?? "low") as "low" | "medium" | "high";
                const riskColor = {
                  low: "text-success bg-success/10 border-success/30",
                  medium: "text-warning bg-warning/10 border-warning/30",
                  high: "text-destructive bg-destructive/10 border-destructive/30",
                }[risk];
                return (
                  <li
                    key={r.id}
                    className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background/50 p-4"
                  >
                    <div className="min-w-[200px] flex-1">
                      <p className="truncate font-semibold text-foreground">
                        {r.title ?? "Untitled contract"}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                        {r.tool_slug ? ` · ${r.tool_slug}` : ""}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider",
                        riskColor,
                      )}
                    >
                      {risk}
                      {r.risk_score != null ? ` · ${r.risk_score}` : ""}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() =>
                          r.result && exportAnalysisPdf(r.result, { title: r.title ?? undefined })
                        }
                        disabled={!r.result}
                      >
                        <Download className="h-3.5 w-3.5" /> PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAnalysis(r.id)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          NOT LEGAL ADVICE. FOR INFORMATIONAL PURPOSES ONLY.
        </p>
      </main>
      <Footer />
    </div>
  );
}
